import { TrashIcon } from '@heroicons/react/24/outline'
import DropDown from '@lib/drop-down'
import React, { useMemo, useCallback } from 'react'
import clsx from 'clsx'

const ReservedTickets = ({ model, ticketOptions }) => {
  // Memoize ticket options to prevent recreation on every render
  const ticketOptionsWithOnClick = useMemo(() => {
    if (!ticketOptions || ticketOptions.length === 0) {
      console.log('No ticket options available')
      return []
    }
    
    return ticketOptions.map(option => ({
      ...option,
      quantity:
        model.seatsInBasket?.filter(t => t.ticket?.type?.id === option.value)
          ?.length || 0,
      ticketTypeId: option.ticket?.ticketType?.id || option.value,
      band: option.band
    }))
  }, [ticketOptions, model.seatsInBasket])

  // Create a stable callback for handling ticket selection
  const handleTicketSelect = useCallback((option, seat) => {
    // Create a new seatActions object to trigger Ibiza state update
    const newSeatActions = { ...model.seatActions }
    
    if (!newSeatActions[seat.id]) {
      newSeatActions[seat.id] = {
        ticket: {
          type: { id: option.ticketTypeId },
          band: option.band
        }
      }
    } else {
      newSeatActions[seat.id] = {
        ...newSeatActions[seat.id],
        ticket: {
          type: { id: option.ticketTypeId },
          band: option.band
        }
      }
    }
    
    // Reassign the entire object to trigger proxy update
    model.seatActions = newSeatActions
  }, [])

  const Ticket = ({ seat }) => {
    // Memoize selected value to prevent unnecessary recalculation
    const selected = useMemo(() => {
      // First check if there's a seat action for this seat
      const seatAction = model.seatActions[seat.id]
      if (seatAction?.ticket?.type?.id) {
        return ticketOptionsWithOnClick.find(
          o => o?.value === seatAction.ticket.type.id && 
               o?.band?.id === seatAction.ticket?.band?.id
        ) || null
      }
      
      // Otherwise check the basket
      const seatInBasket = model.seatsInBasket.find(t => t.id === seat.id)
      if (!seatInBasket?.ticket?.type?.id) return null
      
      return ticketOptionsWithOnClick.find(
        o => o?.value === seatInBasket.ticket.type.id && 
             o?.band?.id === seatInBasket.ticket?.band?.id
      ) || null
    }, [seat.id, model.seatsInBasket, model.seatActions, ticketOptionsWithOnClick])
    
    // Memoize filtered options
    const filteredOptions = useMemo(() => {
      if (!model.availableSeats?.[seat.id]) {
        console.log('No availability data for seat', seat.id, 'using all options:', ticketOptionsWithOnClick)
        return ticketOptionsWithOnClick.map(option => ({
          ...option,
          onClick: () => handleTicketSelect(option, seat)
        }))
      }
      
      const availData = model.availableSeats[seat.id]
      console.log('Available seats data for', seat.id, ':', availData)
      console.log('Ticket options:', ticketOptionsWithOnClick)
      
      // If all bands are in nok (not ok), we might need to show them anyway
      // This can happen when tickets are already in the basket
      const hasNoAvailableOptions = availData.ok?.length === 0 && availData.basket?.length === 0
      
      const filtered = ticketOptionsWithOnClick.filter(o => {
        const bandId = o.band?.id
        if (!bandId) {
          return true // If no band ID, allow the option
        }
        
        // If this ticket is already in the basket, always show it
        const currentTicketBandId = model.currentBasket.find(t => t.seat?.id === seat.id)?.ticket?.band?.id
        if (currentTicketBandId && bandId === currentTicketBandId) {
          return true
        }
        
        const isOk = availData.ok?.includes(bandId)
        const isInBasket = availData.basket?.includes(bandId)
        
        // If nothing is available and this seat already has a ticket, show all options
        if (hasNoAvailableOptions && model.currentBasket.find(t => t.seat?.id === seat.id)) {
          return true
        }
        
        return isOk || isInBasket
      })
      
      console.log('Filtered options for seat', seat.id, ':', filtered)
      
      return filtered.map(option => ({
        ...option,
        onClick: () => handleTicketSelect(option, seat)
      }))
    }, [seat.id, model.availableSeats, ticketOptionsWithOnClick, handleTicketSelect])
    
    // Check if this seat is marked for removal
    const isMarkedForRemoval = model.seatActions[String(seat.id)]?.removeFromBasket
    
    return (
      <div className={clsx(
        'flex items-center w-full gap-3 relative',
        isMarkedForRemoval && 'opacity-60'
      )}>
        <div className={clsx(
          'text-gray-900 dark:text-white font-semibold w-10 text-left',
          isMarkedForRemoval && 'line-through decoration-2 decoration-red-500'
        )}>
          {seat.name}
        </div>
        <div className={clsx(
          'flex-1 relative',
          isMarkedForRemoval && 'line-through decoration-2 decoration-red-500'
        )}>
          <DropDown
            options={filteredOptions}
            selected={selected}
            className={clsx('w-full', isMarkedForRemoval && 'pointer-events-none opacity-70')}
            placeholder='Select ticket type'
            optionClassName='hover:bg-gray-100 dark:hover:bg-gray-700'
          />
        </div>
        <button
          onClick={() => {
            // Directly manipulate seatActions here since handleRemoveTicket isn't accessible
            const seatIdStr = String(seat.id)
            const newSeatActions = { ...model.seatActions }
            
            if (newSeatActions[seatIdStr]?.removeFromBasket) {
              delete newSeatActions[seatIdStr]
            } else {
              newSeatActions[seatIdStr] = { removeFromBasket: true }
              
              // Check if all seats will be marked for removal
              const remainingSeats = model.seatsInBasket.filter(s => {
                const sid = String(s.id)
                return sid !== seatIdStr && !newSeatActions[sid]?.removeFromBasket
              })
              
              // If no seats will remain, clear meal deal selection
              if (remainingSeats.length === 0 && model.mealDealSelection) {
                model.mealDealSelection = null
              }
            }
            
            model.seatActions = newSeatActions
          }}
          className={clsx(
            'hover:border rounded p-1 border-red-400 hover:bg-red-100 dark:hover:bg-red-900/20',
            isMarkedForRemoval && 'bg-red-100 dark:bg-red-900/20 border border-red-400'
          )}
          title={isMarkedForRemoval ? 'Click to undo removal' : 'Remove ticket'}
        >
          <TrashIcon className='w-4 h-4 text-red-600 dark:text-red-400' />
        </button>
      </div>
    )
  }

  return (
    <div className='flex flex-col w-full py-4 gap-2 overflow-visible'>
      {model.seatsInBasket?.map(seat => (
        <Ticket seat={seat} key={seat.id} />
      ))}
    </div>
  )
}

export default ReservedTickets
